/**
 * admin-instructors.ts - 강사(Instructor) 관련 Firestore 서비스
 *
 * 경로: users/{ownerUid}/centers/{centerId}/instructors/{instructorId}
 */

import { collection, doc, getDoc, getDocs, updateDoc, deleteField } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import type { Instructor } from "@vlab-corporation/vlab-pilates-service-interface/staff";
export type { Instructor };

/** Firestore에 저장된 강사 상세 정보 (Instructor + 추가 필드) */
export interface InstructorDetail extends Instructor {
  email?: string;
  studiomateMapping?: {
    staffId: number;
    staffName: string;
    mappedAt: string;
    mappedBy: string;
  };
}

/**
 * 센터의 강사 목록을 조회한다.
 */
export async function getInstructors(ownerUid: string, centerId: number): Promise<Instructor[]> {
  const db = getFirebaseFirestore();
  const colRef = collection(db, "users", ownerUid, "centers", String(centerId), "instructors");
  const snap = await getDocs(colRef);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Instructor);
}

/**
 * 강사의 시니어 상태를 업데이트한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param instructorId - 강사 ID
 * @param seniorSince - 시니어 적용 시작일 (YYYY-MM-DD). null이면 시니어 해제
 */
export async function updateInstructorSeniorStatus(
  ownerUid: string,
  centerId: number,
  instructorId: string,
  seniorSince: string | null,
): Promise<void> {
  const db = getFirebaseFirestore();
  const ref = doc(db, "users", ownerUid, "centers", String(centerId), "instructors", instructorId);

  if (seniorSince != null) {
    await updateDoc(ref, { seniorSince });
  } else {
    await updateDoc(ref, { seniorSince: deleteField() });
  }
}

/**
 * 강사의 상세 정보를 조회한다 (email, studiomateMapping 포함).
 *
 * @returns 강사 상세 정보 또는 null (문서가 없는 경우)
 */
export async function getInstructorDetail(
  ownerUid: string,
  centerId: number,
  instructorId: string,
): Promise<InstructorDetail | null> {
  const db = getFirebaseFirestore();
  const ref = doc(db, "users", ownerUid, "centers", String(centerId), "instructors", instructorId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as InstructorDetail;
}
